import { DealLineItem, GetDealResponse, PRICING_STRATEGY, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { Table } from '@schuettflix/react-components';
import { DealLineItemRow } from './DealLineItemRow';
import { useMemo, useState } from 'react';
import { getCompatibleDealLineItems } from '../utils/getCompatibleDealLineItems';
import { useTranslation } from 'react-i18next';

type DealLineItemsTableProps = {
    dealLineItems: DealLineItem[];
    organizations: NonNullable<GetDealResponse>['organizations'];
    onSelectionChange: (selection: {
        selectedDealLineItemIds: string[];
        lineItemIdsWithDeselectedRemarks: string[];
        manualSelectedProductTypesMap: Map<string, PRODUCT_TYPES>;
        mainPricingStrategy: PRICING_STRATEGY | null;
    }) => void;
};
export function DealLineItemsTable({ dealLineItems, organizations, onSelectionChange }: DealLineItemsTableProps) {
    const { t } = useTranslation();
    const [selectedLineItemIdsSet, setSelectedLineItemIdsSet] = useState(new Set<string>());
    const [lineItemIdsWithDeselectedRemarksSet, setLineItemIdsWithDeselectedRemarksSet] = useState(new Set<string>());
    const [manualSelectedProductTypesMap, setManualSelectedProductTypeMap] = useState(new Map<string, PRODUCT_TYPES>());

    const { compatibleDealLineItemIds } = useMemo(() => {
        const selectedDealLineItemIds = [...selectedLineItemIdsSet];
        const { compatibleDealLineItemIds, mainPricingStrategy, isValidSelection } = getCompatibleDealLineItems({
            availableDealLineItems: dealLineItems.map(dealLineItem => {
                if (dealLineItem.product.productTypeConfig.mappingStrategy === 'MANUAL') {
                    dealLineItem.product.productType =
                        manualSelectedProductTypesMap.get(dealLineItem.salesforceMetaData.opportunityLineItemId) ??
                        null;
                }

                return dealLineItem;
            }),
            selectedDealLineItemIds,
        });

        if (!isValidSelection) {
            setSelectedLineItemIdsSet(new Set());
        } else {
            onSelectionChange({
                selectedDealLineItemIds,
                lineItemIdsWithDeselectedRemarks: [...lineItemIdsWithDeselectedRemarksSet],
                manualSelectedProductTypesMap,
                mainPricingStrategy,
            });
        }

        return { compatibleDealLineItemIds };
    }, [
        dealLineItems,
        selectedLineItemIdsSet,
        onSelectionChange,
        lineItemIdsWithDeselectedRemarksSet,
        manualSelectedProductTypesMap,
    ]);

    return (
        <Table
            columns={[
                {
                    id: 'checkbox',
                },
                {
                    id: 'type',
                    isRowHeader: true,
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.type'),
                },
                {
                    id: 'productName',
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.productName'),
                },
                {
                    id: 'quantity',
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.quantity'),
                },
                {
                    id: 'materialPrice',
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.materialPrice'),
                },
                {
                    id: 'transportPrice',
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.transportPrice'),
                },
                {
                    id: 'francoPrice',
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.francoPrice'),
                },
                {
                    id: 'salesPrice',
                    textValue: t('salesforceDealImporter.dealLineItemsTable.columns.salesPrice'),
                },
                {
                    id: 'positions',
                },
            ]}
            enableHeader
            locale="de-DE"
            size="md"
        >
            {dealLineItems.map(dealLineItem => (
                <DealLineItemRow
                    key={dealLineItem.salesforceMetaData.opportunityLineItemId}
                    dealLineItem={dealLineItem}
                    organizations={organizations}
                    compatibleDealLineItemIds={compatibleDealLineItemIds}
                    isSelected={selectedLineItemIdsSet.has(dealLineItem.salesforceMetaData.opportunityLineItemId)}
                    hasSelectedRemark={
                        !lineItemIdsWithDeselectedRemarksSet.has(dealLineItem.salesforceMetaData.opportunityLineItemId)
                    }
                    manualSelectedProductType={
                        manualSelectedProductTypesMap.get(dealLineItem.salesforceMetaData.opportunityLineItemId) ?? null
                    }
                    onChange={isSelected => {
                        if (isSelected) {
                            setSelectedLineItemIdsSet(
                                new Set(
                                    selectedLineItemIdsSet.add(dealLineItem.salesforceMetaData.opportunityLineItemId)
                                )
                            );
                            return;
                        } else if (
                            selectedLineItemIdsSet.delete(dealLineItem.salesforceMetaData.opportunityLineItemId)
                        ) {
                            setSelectedLineItemIdsSet(new Set(selectedLineItemIdsSet));
                        }
                    }}
                    onChangeManualSelectedProductType={value => {
                        if (!value) {
                            manualSelectedProductTypesMap.delete(dealLineItem.salesforceMetaData.opportunityLineItemId);
                            return;
                        } else {
                            manualSelectedProductTypesMap.set(
                                dealLineItem.salesforceMetaData.opportunityLineItemId,
                                value
                            );
                        }
                        setManualSelectedProductTypeMap(new Map(manualSelectedProductTypesMap));
                    }}
                    onChangeRemarkSelection={hasSelectedRemark => {
                        if (!hasSelectedRemark) {
                            setLineItemIdsWithDeselectedRemarksSet(
                                new Set(
                                    lineItemIdsWithDeselectedRemarksSet.add(
                                        dealLineItem.salesforceMetaData.opportunityLineItemId
                                    )
                                )
                            );
                            return;
                        } else if (
                            lineItemIdsWithDeselectedRemarksSet.delete(
                                dealLineItem.salesforceMetaData.opportunityLineItemId
                            )
                        ) {
                            setLineItemIdsWithDeselectedRemarksSet(new Set(lineItemIdsWithDeselectedRemarksSet));
                        }
                    }}
                />
            ))}
        </Table>
    );
}
